"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sendTestNotificationSchema = exports.updatePreferencesSchema = exports.markReadSchema = exports.inboxQuerySchema = exports.notificationCategorySchema = exports.NOTIFICATION_DEFINITIONS = exports.NOTIFICATION_CATEGORY_DESCRIPTIONS = exports.NOTIFICATION_CATEGORY_LABELS = exports.NOTIFICATION_CATEGORIES = void 0;
exports.notificationDefinition = notificationDefinition;
exports.escalationsIn = escalationsIn;
exports.buildDeepLink = buildDeepLink;
const zod_1 = require("zod");
const schemas_1 = require("./schemas");
// §L22 — the notification catalogue, as data.
//
// The catalogue names 72 notifications. S8 builds the *engine* and registers only
// the ones whose triggers already exist, per the master plan: "Register them as
// later slices deliver the triggering features. Do not build all 72 now."
//
// Three rules from §L22's closing paragraph are encoded here rather than left to
// each caller, because each of them is the kind of rule that quietly stops being
// true when it lives in prose:
//
// 1. **Every actionable notification deep-links to the specific record.** The link
//    is a pattern declared on the definition, filled from parameters at emit time —
//    not a string a call site passes in. A call site that forgets a parameter fails
//    loudly; a call site that hands over `/dashboard` cannot exist.
// 2. **Escalation notifications cannot be disabled.** `isEscalation` is read by the
//    preferences service, so the rule is enforced where preferences are resolved
//    rather than only hidden on the screen.
// 3. **Preferences are per category**, which is what NOTIF-02 offers. A user with
//    nine decisions to make will make them; a user with seventy-two will make none.
/** The nine §L22 headings. Preferences are set against these. */
exports.NOTIFICATION_CATEGORIES = [
    'requisition_approval',
    'job_posting',
    'application_screening',
    'assessment',
    'interview_scheduling',
    'scorecards_decisions',
    'offer_hire',
    'candidate_pool',
    'system_admin',
];
exports.NOTIFICATION_CATEGORY_LABELS = {
    requisition_approval: 'Requisitions and approvals',
    job_posting: 'Jobs and posting',
    application_screening: 'Applications and screening',
    assessment: 'Assessments',
    interview_scheduling: 'Interview scheduling',
    scorecards_decisions: 'Scorecards and decisions',
    offer_hire: 'Offers and hiring',
    candidate_pool: 'Candidates and talent pool',
    system_admin: 'System and admin',
};
exports.NOTIFICATION_CATEGORY_DESCRIPTIONS = {
    requisition_approval: 'Approvals waiting on you, and what happened to yours.',
    job_posting: 'Jobs opening, ageing, expiring or closing.',
    application_screening: 'New applications, screening backlogs and duplicates.',
    assessment: 'Assessments assigned, submitted, expired or waiting to be graded.',
    interview_scheduling: 'Interviews booked, moved, cancelled or starting soon.',
    scorecards_decisions: 'Scorecards due, and decisions waiting on you.',
    offer_hire: 'Offers moving, and joining dates approaching.',
    candidate_pool: 'Withdrawals, follow-ups and expiring holds.',
    system_admin: 'Invites, role changes, delegation and system messages.',
};
/**
 * The registered notifications.
 *
 * Four have live triggers today — invites, role changes and delegation all landed
 * in S3/S4 — so they are wired end to end rather than described. Four more are the
 * §L22 escalations, registered without triggers on purpose: they are what makes
 * "an escalation cannot be switched off" a rule the preferences screen and the
 * preferences service can both be tested against now, rather than a sentence
 * somebody has to remember in S18.
 *
 * The rest of the 72 arrive with the slices that fire them.
 */
exports.NOTIFICATION_DEFINITIONS = [
    {
        key: 'user.invited',
        catalogueId: 'N-66',
        category: 'system_admin',
        label: 'Invite sent',
        description: 'A new colleague is invited and needs to set a password.',
        // Email only: an invited person has no account to receive an in-app message in.
        channels: ['email'],
        deepLinkPattern: '/accept-invite?token=:token',
        isUrgent: true,
        templateKey: 'user.invited',
    },
    {
        key: 'user.invite_unaccepted',
        catalogueId: 'N-67',
        category: 'system_admin',
        label: 'Invite still unaccepted',
        description: 'Nobody has used an invite you sent three days ago.',
        channels: ['in_app'],
        deepLinkPattern: '/admin/users/:userId',
        templateKey: 'user.invite_unaccepted',
    },
    {
        key: 'user.access_changed',
        catalogueId: 'N-68',
        category: 'system_admin',
        label: 'Your role or scope changed',
        description: 'What you can see or do in the system has been changed.',
        channels: ['in_app', 'email'],
        // §L22 gives this row no deep link, and honestly so: nothing needs doing.
        isUrgent: true,
        templateKey: 'user.access_changed',
    },
    {
        key: 'delegation.active',
        catalogueId: 'N-69',
        category: 'system_admin',
        label: 'Delegation started',
        description: 'You are now covering approvals for somebody else.',
        channels: ['in_app', 'email'],
        // §L22 N-69 deep link: REQ-04, the list of approvals the delegate now covers. Its menu
        // item is gone (workflow review 2026-10-29: hire requests are approved on the board), but
        // the page stays as this message's landing, because a board is a module root (#3).
        deepLinkPattern: '/approvals',
        isUrgent: true,
        templateKey: 'delegation.active',
    },
    {
        key: 'system.test',
        catalogueId: 'N-00',
        category: 'system_admin',
        label: 'Test notification',
        description: 'Sent by HR to check that notifications and email work.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/admin/email',
        isUrgent: true,
        templateKey: 'system.test',
    },
    {
        /**
         * The escalation the HR Lead's test button fires.
         *
         * It exists rather than reusing one of the four §L22 escalations below for a
         * reason worth stating: all four point at screens later slices build, so a test
         * run today would send a real email with a link to a page that does not exist —
         * a poor first impression of the one rule this slice calls the most important.
         * This one points somewhere real, and being a genuine escalation (it cannot be
         * disabled, and it copies its subject) is what makes the test prove anything.
         */
        key: 'system.test_escalation',
        catalogueId: 'N-00e',
        category: 'system_admin',
        label: 'Test escalation',
        description: 'Sent by HR to check that the delay-and-escalate chain works.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/admin/email',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    // --- S9: requisitions and approvals (§L22 N-01 to N-08) --------------------
    {
        key: 'requisition.approval_requested',
        catalogueId: 'N-01',
        category: 'requisition_approval',
        label: 'Approval requested',
        description: 'A requisition reached a step you approve.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId?decide=:stepId',
        isUrgent: true,
        templateKey: 'requisition.action',
    },
    {
        key: 'requisition.approval_pending',
        catalogueId: 'N-02',
        category: 'requisition_approval',
        label: 'Approval still waiting',
        description: 'A requisition has waited on you for 48 hours, and again at 96.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId?decide=:stepId',
        templateKey: 'requisition.action',
    },
    {
        // Not in §L22's closing list of escalations, so a recipient may switch it off
        // like any other requisition message.
        key: 'requisition.approval_overdue',
        catalogueId: 'N-03',
        category: 'requisition_approval',
        label: 'Approval overdue — manager told',
        description: 'An approval has waited 120 hours, so the approver’s manager is told.',
        channels: ['email'],
        deepLinkPattern: '/requisitions/:requisitionId',
        isUrgent: true,
        templateKey: 'requisition.update',
    },
    {
        key: 'requisition.approved',
        catalogueId: 'N-04',
        category: 'requisition_approval',
        label: 'Requisition approved',
        description: 'Every step approved it. The time-to-fill clock has started.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId',
        isUrgent: true,
        templateKey: 'requisition.update',
    },
    {
        key: 'requisition.rejected',
        catalogueId: 'N-05',
        category: 'requisition_approval',
        label: 'Requisition rejected',
        description: 'An approver rejected your requisition, with a reason.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId',
        isUrgent: true,
        templateKey: 'requisition.update',
    },
    {
        key: 'requisition.sent_back',
        catalogueId: 'N-06',
        category: 'requisition_approval',
        label: 'Requisition sent back',
        description: 'An approver sent your requisition back with a comment to change it.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId/edit',
        isUrgent: true,
        templateKey: 'requisition.update',
    },
    {
        key: 'requisition.on_hold',
        catalogueId: 'N-07',
        category: 'requisition_approval',
        label: 'Requisition on hold',
        description: 'A requisition was paused, with a reason. Its clock is paused too.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId',
        templateKey: 'requisition.update',
    },
    {
        // §L22: JOB-02 — the job wizard's first step for this requisition (S11).
        key: 'requisition.no_job',
        catalogueId: 'N-08',
        category: 'requisition_approval',
        label: 'Approved, no job yet',
        description: 'A requisition was approved 72 hours ago and has no job.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/new/:requisitionId',
        templateKey: 'requisition.update',
    },
    {
        // Workflow review 2026-10-29 (section A): the approved request has made its draft job.
        key: 'requisition.job_ready',
        catalogueId: 'N-98',
        category: 'requisition_approval',
        label: 'Draft job ready',
        description: 'A request was approved and its draft job was made. Fill it in and open it.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId',
        isUrgent: true,
        templateKey: 'requisition.update',
    },
    // --- §L22 Job & posting (S13). Every one lands on the job hub, or its postings tab.
    {
        key: 'job.opened',
        catalogueId: 'N-09',
        category: 'job_posting',
        label: 'Job opened',
        description: 'A job you manage or interview for has opened to candidates.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId',
        templateKey: 'job.update',
    },
    {
        key: 'posting.expiring',
        catalogueId: 'N-10',
        category: 'job_posting',
        label: 'Posting expiring',
        description: 'A posting on one of your jobs comes down in three days.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/postings',
    },
    {
        // Silent through S13–S14, when nobody could advance a candidate (DECISIONS-LOG
        // S13 Q11). S15's sorting sweep sends it, once per job.
        key: 'job.aging',
        catalogueId: 'N-11',
        category: 'job_posting',
        label: 'Job ageing, no one moved forward',
        description: 'A job has been open 14 days and no candidate has moved past screening.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId',
        templateKey: 'job.update',
    },
    {
        key: 'job.past_target_close',
        catalogueId: 'N-12',
        category: 'job_posting',
        label: 'Job past its target close date',
        description: 'A job is still open after the date it was meant to close. Repeats daily.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId',
        templateKey: 'job.update',
    },
    {
        key: 'job.cancelled',
        catalogueId: 'N-13',
        category: 'job_posting',
        label: 'Job cancelled',
        description: 'A job you work on was cancelled, with the reason.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId',
        isUrgent: true,
        templateKey: 'job.update',
    },
    {
        // S14 — CAND-06 exists now, so the §L2 tier 4–6 flag S13 started raising can
        // tell someone. In-app only, so no email template.
        key: 'candidate.duplicate_flagged',
        catalogueId: 'N-17',
        category: 'application_screening',
        label: 'Possible duplicate candidate',
        description: 'A new applicant may already be in the system. Compare them and merge, or mark them as different people.',
        channels: ['in_app'],
        deepLinkPattern: '/candidates/duplicates/:flagId',
    },
    // S15 — N-15, N-16 and N-18 land on SCR-01 or SCR-04, which S15 builds; no slice
    // owned them (DECISIONS-LOG S15 Q7).
    {
        key: 'screening.backlog',
        catalogueId: 'N-15',
        category: 'application_screening',
        label: 'Applications waiting to be sorted',
        description: 'Once a day, when 25 or more applications on one of your jobs are waiting for a first look.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/sort',
    },
    {
        key: 'screening.overdue',
        catalogueId: 'N-16',
        category: 'application_screening',
        label: 'Application waiting past its screening time',
        description: 'An application has waited in screening longer than the job allows.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/sort',
        templateKey: 'screening.update',
    },
    {
        key: 'screening.auto_reject_rate',
        catalogueId: 'N-18',
        category: 'application_screening',
        label: 'Many applicants turned away automatically',
        description: 'Once a day, when a job\'s screening questions turn away more than 60% of its applicants.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/auto-rejected',
        templateKey: 'screening.update',
    },
    // S16 — §L9 promises "notified on completion" for a background bulk action; §L22 had
    // no row for it, so N-73 was added (DECISIONS-LOG S16 Q5). The link opens the result.
    {
        key: 'pipeline.bulk_finished',
        catalogueId: 'N-73',
        category: 'application_screening',
        label: 'Bulk action finished',
        description: 'A bulk action you started on more than 25 people has finished — how many were updated, and which failed.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/pipeline?action=:actionId',
    },
    // S17 — the online test. N-20 and N-21 go to the candidate, who has no account, so they
    // are sent through the outbox rather than to a user; they are registered for their
    // link, which is built from the pattern here, as N-66's is.
    {
        key: 'assessment.assigned',
        catalogueId: 'N-20',
        category: 'assessment',
        label: 'Test assigned',
        description: 'The candidate is sent their test link.',
        channels: ['email'],
        deepLinkPattern: '/test?token=:token',
        isUrgent: true,
        templateKey: 'assessment.link',
    },
    {
        key: 'assessment.reminder',
        catalogueId: 'N-21',
        category: 'assessment',
        label: 'Test reminder',
        description: 'The candidate is reminded 48 and 12 hours before their test link expires.',
        channels: ['email'],
        deepLinkPattern: '/test?token=:token',
        isUrgent: true,
        templateKey: 'assessment.reminder',
    },
    {
        key: 'assessment.expired',
        catalogueId: 'N-22',
        category: 'assessment',
        label: 'Test link expired',
        description: 'A candidate did not open their test before the link expired. The candidate is told as well.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/assessment',
        templateKey: 'assessment.update',
    },
    {
        key: 'assessment.submitted',
        catalogueId: 'N-23',
        category: 'assessment',
        label: 'Test submitted',
        description: 'A candidate finished their test. The score is on the results screen.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/assessment',
    },
    // S18 — marking (ASM-06) and exam sessions (ASM-05). The candidate's exam emails
    // (N-26's candidate half, N-74 booked, N-75 moved, N-76 cancelled) go through the
    // outbox, as N-20's do; they carry no link, because there is nothing to open.
    {
        key: 'assessment.grading_pending',
        catalogueId: 'N-24',
        category: 'assessment',
        label: 'Written answers to mark',
        description: 'A test you mark has answers waiting. Sent when it is submitted, and again a day later if still waiting.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/assessments/marking/:attemptId',
        templateKey: 'assessment.marking',
    },
    {
        key: 'assessment.grading_overdue',
        catalogueId: 'N-25',
        category: 'assessment',
        label: 'Marking overdue',
        description: 'Written answers have waited three days for marking. Sent to the marker and HR.',
        channels: ['email'],
        deepLinkPattern: '/assessments/marking/:attemptId',
        templateKey: 'assessment.marking',
    },
    {
        key: 'exam_session.tomorrow',
        catalogueId: 'N-26',
        category: 'assessment',
        label: 'Exam session tomorrow',
        description: 'An exam you invigilate is tomorrow — how many are booked, when and where.',
        channels: ['email'],
        deepLinkPattern: '/assessments/sessions/:sessionId',
        templateKey: 'exam.update',
    },
    // S19 — interview scheduling. The candidate's half of N-27–N-33 goes through the outbox
    // (no account, D16); N-27 and N-28 are registered for the link to the booking page,
    // which every candidate email about an interview carries (§L22 S19 notes).
    {
        key: 'interview.slots_offered',
        catalogueId: 'N-27',
        category: 'interview_scheduling',
        label: 'Interview times sent',
        description: 'The candidate is sent times to choose from, with a one-click link.',
        channels: ['email'],
        deepLinkPattern: '/interview?token=:token',
        isUrgent: true,
        templateKey: 'interview.times',
    },
    {
        key: 'interview.slot_reminder',
        catalogueId: 'N-28',
        category: 'interview_scheduling',
        label: 'Reminder to choose an interview time',
        description: 'The candidate is reminded 24 and 48 hours after the times went, by email while WhatsApp is not connected (S19 Q9).',
        channels: ['email'],
        deepLinkPattern: '/interview?token=:token',
        isUrgent: true,
        templateKey: 'interview.times_reminder',
    },
    {
        key: 'interview.scheduled',
        catalogueId: 'N-29',
        category: 'interview_scheduling',
        label: 'Interview booked',
        description: 'An interview you are on has a time. The candidate is sent it too, with a link to confirm.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId',
        templateKey: 'interview.update',
    },
    {
        key: 'interview.reminder',
        catalogueId: 'N-30',
        category: 'interview_scheduling',
        label: 'Interview tomorrow',
        description: 'A day before an interview you are on. The candidate is reminded too.',
        channels: ['email'],
        deepLinkPattern: '/interviews/:interviewId',
        templateKey: 'interview.update',
    },
    {
        // §L22: straight into the cockpit (INT-01), where the panelist scores.
        key: 'interview.starting_soon',
        catalogueId: 'N-31',
        category: 'interview_scheduling',
        label: 'Interview starting soon',
        description: 'An hour before an interview you are on.',
        channels: ['in_app'],
        deepLinkPattern: '/interviews/:interviewId/scorecard',
        isUrgent: true,
    },
    {
        key: 'interview.rescheduled',
        catalogueId: 'N-32',
        category: 'interview_scheduling',
        label: 'Interview moved',
        description: 'An interview you are on is moving to another time — or, to the recruiter, a panelist cannot make it (S19 Q10).',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId',
        isUrgent: true,
        templateKey: 'interview.update',
    },
    {
        key: 'interview.cancelled',
        catalogueId: 'N-33',
        category: 'interview_scheduling',
        label: 'Interview cancelled',
        description: 'An interview you are on will not happen.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId',
        isUrgent: true,
        templateKey: 'interview.update',
    },
    {
        key: 'interview.candidate_no_show',
        catalogueId: 'N-34',
        category: 'interview_scheduling',
        label: 'Candidate did not come',
        description: 'A candidate missed their interview. Decide from their application what happens next.',
        channels: ['in_app'],
        deepLinkPattern: '/applications/:applicationId',
    },
    {
        key: 'interview.panelist_no_show',
        catalogueId: 'N-35',
        category: 'interview_scheduling',
        label: 'Panelist did not come',
        description: 'A panelist missed an interview. Sent to the recruiter and HR.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId',
        templateKey: 'interview.update',
    },
    {
        key: 'interview.unscheduled_overdue',
        catalogueId: 'N-36',
        category: 'interview_scheduling',
        label: 'Interview still not scheduled',
        description: 'An applicant has waited in an interview stage past its time with no interview booked.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/schedule',
        templateKey: 'interview.update',
    },
    // S19 Q8: §L22 had no row for this; N-77 was added.
    {
        key: 'interview.candidate_left',
        catalogueId: 'N-77',
        category: 'interview_scheduling',
        label: 'Booked interview — the candidate left the stage',
        description: 'An interview is still booked for someone who was rejected, withdrew or moved on. Cancel it or keep it.',
        channels: ['in_app'],
        deepLinkPattern: '/interviews/:interviewId',
    },
    {
        // Not a §L22 row: the catalogue has no message for an SLA rule's own
        // escalation rungs (F16.07). One generic escalation carries them all.
        key: 'sla.escalated',
        catalogueId: 'F16.07',
        category: 'requisition_approval',
        label: 'Overdue — escalated',
        description: 'Something passed its time limit, so the next person up is told.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/requisitions/:requisitionId',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    {
        // S15: application stage clocks escalate too, and a requisition link would be
        // wrong for them (non-negotiable #3) — this one opens the application itself.
        key: 'sla.escalated_application',
        catalogueId: 'F16.07',
        category: 'application_screening',
        label: 'Application overdue — escalated',
        description: 'An application passed its time limit in a stage, so the next person up is told.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/applications/:applicationId',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    // --- Scorecards (S20): N-37 and N-38 to the panelist; N-39 and N-40 escalate. ---
    {
        key: 'scorecard.due',
        catalogueId: 'N-37',
        category: 'scorecards_decisions',
        label: 'Scorecard due',
        description: 'An interview you sat on is over — your scorecard is due.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/scorecard',
        templateKey: 'scorecard.update',
    },
    {
        key: 'scorecard.overdue',
        catalogueId: 'N-38',
        category: 'scorecards_decisions',
        label: 'Scorecard overdue',
        description: 'Your scorecard is past its deadline. Sent at the deadline and a working day later.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/scorecard',
        isUrgent: true,
        templateKey: 'scorecard.update',
    },
    // --- C6 (ADR 0077): the panelist's own scorecard link. Sent by the outbox, never stored readable
    // in the inbox: the link is the scorecard page itself (INT-04), so the token is in it. ---
    {
        key: 'scorecard.link',
        catalogueId: 'N-101',
        category: 'scorecards_decisions',
        label: 'Your scorecard link',
        description: 'An interview you sit on is booked: your own link to give your mark, no sign-in needed.',
        channels: ['email'],
        deepLinkPattern: '/scorecard?token=:token',
        isUrgent: true,
        templateKey: 'scorecard.link',
    },
    {
        key: 'scorecard.link_reminder',
        catalogueId: 'N-102',
        category: 'scorecards_decisions',
        label: 'Scorecard link reminder',
        description: 'A panelist without an account has not given their mark by the deadline: their link again.',
        channels: ['email'],
        deepLinkPattern: '/scorecard?token=:token',
        isUrgent: true,
        templateKey: 'scorecard.link',
    },
    // --- Round decisions (S21): N-41, N-42 and N-44 to the decider; N-45 and N-78 to the HR Lead. ---
    {
        key: 'decision.due',
        catalogueId: 'N-41',
        category: 'scorecards_decisions',
        label: 'Decision due',
        description: "Every scorecard for a round is in — it's yours to decide.",
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/debrief',
        templateKey: 'decision.update',
    },
    {
        key: 'decision.overdue',
        catalogueId: 'N-42',
        category: 'scorecards_decisions',
        label: 'Decision overdue',
        description: 'A round you decide has waited a working day, then two.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/debrief',
        isUrgent: true,
        templateKey: 'decision.update',
    },
    {
        key: 'decision.tie_break',
        catalogueId: 'N-44',
        category: 'scorecards_decisions',
        label: 'Tie-break required',
        description: 'A round split evenly under the majority rule, and the call is yours.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/interviews/:interviewId/debrief',
        isUrgent: true,
        templateKey: 'decision.update',
    },
    {
        key: 'decision.overridden',
        catalogueId: 'N-45',
        category: 'scorecards_decisions',
        label: 'Decision rule overridden',
        description: 'Someone else in HR went against a round’s verdict, with a reason.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/decisions',
    },
    // S21 Q6: §L22 had no row for this; N-78 was added.
    {
        key: 'candidate.integrity_review',
        catalogueId: 'N-78',
        category: 'scorecards_decisions',
        label: 'Integrity concern — review do-not-rehire',
        description: 'Someone was rejected for an integrity concern, so do-not-rehire is set. Review it, and clear it if it was wrong.',
        channels: ['in_app'],
        deepLinkPattern: '/candidates/:candidateId',
    },
    // --- Final selection and holds (S22): all three open the job's comparison (DEC-02). ---
    // S22 Q1: §L22 had no row for this; N-79 was added.
    {
        key: 'decision.final_selection',
        catalogueId: 'N-79',
        category: 'scorecards_decisions',
        label: 'Final selection recorded',
        description: 'The hiring manager or HR chose who gets the offer.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/compare',
        templateKey: 'comparison.update',
    },
    {
        key: 'hold.expiring',
        catalogueId: 'N-65',
        category: 'candidate_pool',
        label: 'Hold expiring',
        description: 'A silver medalist’s hold ends in three days — release them, or keep them 30 more days.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/compare',
        templateKey: 'comparison.update',
    },
    // S26 — follow-ups, replies and "ask HR".
    {
        key: 'task.due',
        catalogueId: 'N-62',
        category: 'candidate_pool',
        label: 'Follow-up due',
        description: 'A task of yours falls due today.',
        channels: ['in_app'],
        deepLinkPattern: '/tasks/:taskId',
    },
    {
        key: 'task.overdue',
        catalogueId: 'N-63',
        category: 'candidate_pool',
        label: 'Follow-up overdue',
        description: 'A task of yours is a day past its due date.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/tasks/:taskId',
        templateKey: 'task.update',
    },
    // Q9: added in S26 — §L22 had no notice for a reply. One key per landing screen (#3).
    {
        key: 'message.reply_application',
        catalogueId: 'N-82',
        category: 'application_screening',
        label: 'Candidate replied',
        description: 'A candidate answered an email about one of your applications.',
        channels: ['in_app'],
        deepLinkPattern: '/applications/:applicationId',
    },
    {
        key: 'message.reply_candidate',
        catalogueId: 'N-82',
        category: 'application_screening',
        label: 'Candidate replied',
        description: 'A candidate with more than one open application wrote in.',
        channels: ['in_app'],
        deepLinkPattern: '/candidates/:candidateId',
    },
    // Q10 (O15): a hiring manager's question to HR was answered.
    {
        key: 'task.answered',
        catalogueId: 'N-83',
        category: 'requisition_approval',
        label: 'HR answered your question',
        description: 'The recruiter replied to a question you asked about one of your openings.',
        channels: ['in_app'],
        deepLinkPattern: '/requisitions/:requisitionId',
    },
    // S25 — the talent pool. Both in-app only (§L22).
    {
        key: 'pool.strong_match',
        catalogueId: 'N-19',
        category: 'application_screening',
        label: 'Strong pool match on a new job',
        description: 'A job you recruit for has just opened, and people in the talent pool fit it.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/pool',
    },
    {
        key: 'pool.consent_expiring',
        catalogueId: 'N-64',
        category: 'candidate_pool',
        label: 'Pool consent expiring',
        description: 'People in a pool you own reach the end of their consent within 30 days.',
        channels: ['in_app'],
        deepLinkPattern: '/pools/:poolId',
    },
    // S22 registered it; S23 fires it on a decline, an expiry, a no-show or a withdrawal (S23 Q5, Q6).
    {
        key: 'hold.silver_available',
        catalogueId: 'N-55',
        category: 'offer_hire',
        label: 'Silver medalists available',
        description: 'An offer fell through and candidates are on hold for the role.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/compare',
        templateKey: 'comparison.update',
    },
    // --- Offers (S23): every one opens the offer itself (OFR-03), where its action is. ---
    {
        key: 'offer.approval_requested',
        catalogueId: 'N-48',
        category: 'offer_hire',
        label: 'Offer approval requested',
        description: 'An offer reached a step you approve.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        isUrgent: true,
        templateKey: 'offer.update',
    },
    {
        key: 'offer.approval_pending',
        catalogueId: 'N-48',
        category: 'offer_hire',
        label: 'Offer approval still waiting',
        description: 'An offer has waited on you for 24 hours.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        templateKey: 'offer.update',
    },
    {
        key: 'offer.approved',
        catalogueId: 'N-49',
        category: 'offer_hire',
        label: 'Offer approved',
        description: 'Every approver said yes. The letter is made — send it.',
        channels: ['in_app'],
        deepLinkPattern: '/offers/:offerId',
    },
    // S23: §L22 had no row telling the recruiter an approver said no; N-80 was added.
    {
        key: 'offer.approval_rejected',
        catalogueId: 'N-80',
        category: 'offer_hire',
        label: 'Offer sent back by an approver',
        description: 'An approver rejected an offer, with a reason. Fix it and submit it again.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        isUrgent: true,
        templateKey: 'offer.update',
    },
    {
        key: 'offer.expiring',
        catalogueId: 'N-51',
        category: 'offer_hire',
        label: 'Offer expiring',
        description: 'An offer you sent lapses in two days without an answer.',
        channels: ['in_app'],
        deepLinkPattern: '/offers/:offerId',
    },
    {
        key: 'offer.viewed',
        catalogueId: 'N-52',
        category: 'offer_hire',
        label: 'Offer opened',
        description: 'The candidate opened their offer.',
        channels: ['in_app'],
        deepLinkPattern: '/offers/:offerId',
    },
    {
        key: 'offer.accepted',
        catalogueId: 'N-53',
        category: 'offer_hire',
        label: 'Offer accepted',
        description: 'The candidate accepted. The time-to-fill clock has stopped.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        isUrgent: true,
        templateKey: 'offer.update',
    },
    {
        key: 'offer.declined',
        catalogueId: 'N-54',
        category: 'offer_hire',
        label: 'Offer declined',
        description: 'The candidate declined, with a reason.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        isUrgent: true,
        templateKey: 'offer.update',
    },
    // S23: §L22 told the recruiter before an offer lapsed (N-51) but not after; N-81 was added.
    {
        key: 'offer.expired',
        catalogueId: 'N-81',
        category: 'offer_hire',
        label: 'Offer expired',
        description: 'An offer lapsed without an answer.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        templateKey: 'offer.update',
    },
    {
        key: 'offer.no_show',
        catalogueId: 'N-58',
        category: 'offer_hire',
        label: 'Candidate did not join',
        description: 'Someone who accepted did not turn up on their joining date.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId',
        isUrgent: true,
        templateKey: 'offer.update',
    },
    // --- Joining (S24): each opens the hire screen (OFR-04), or the candidate's documents. ---
    {
        key: 'hire.joining_soon',
        catalogueId: 'N-56',
        category: 'offer_hire',
        label: 'Joining date approaching',
        description: 'Someone who accepted joins in seven days.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId/joining',
        templateKey: 'hire.update',
    },
    {
        key: 'hire.joining_unconfirmed',
        catalogueId: 'N-57',
        category: 'offer_hire',
        label: 'Joining not confirmed',
        description: 'A joining date passed and nobody confirmed the joining or recorded that they didn’t come.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/offers/:offerId/joining',
        isUrgent: true,
        templateKey: 'hire.update',
    },
    {
        key: 'hire.documents_outstanding',
        catalogueId: 'N-59',
        category: 'offer_hire',
        label: 'Documents outstanding before joining',
        description: 'Three days before joining, a required document is still not verified.',
        channels: ['in_app'],
        deepLinkPattern: '/candidates/:candidateId',
    },
    {
        // ADR 0083 (owner 2026-11-01 Q8 a): the offer was accepted (with Offers off: the person was Selected), so the job's line
        // manager writes the hire's 30 and 90-day plans before Day 1. Core, not gated by Offers: it fires either way.
        key: 'hire.plans_due',
        catalogueId: 'N-103',
        category: 'offer_hire',
        label: 'Write the 30 and 90-day plans',
        description: 'Someone accepted your job’s offer. Write their 30 and 90-day plans before Day 1; joining waits for them.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/hires/:applicationId/plans',
        isUrgent: true,
        templateKey: 'hire.update',
    },
    // --- The §L22 escalations. N-39 and N-40 are triggered from S20. ---
    {
        key: 'scorecard.escalation_manager',
        catalogueId: 'N-39',
        category: 'scorecards_decisions',
        label: "Scorecard overdue — panelist's manager told",
        description: 'A scorecard is two working days past the interview, so the panelist’s manager is told.',
        channels: ['email'],
        deepLinkPattern: '/interviews/:interviewId/scorecard',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    {
        key: 'scorecard.escalation_hr_lead',
        catalogueId: 'N-40',
        category: 'scorecards_decisions',
        label: 'Scorecard overdue — HR told',
        description: 'A scorecard is four working days past the interview, so HR is told and may waive it.',
        channels: ['email'],
        // §L22 names RPT-04 (S29); until it exists, INT-03, where the HR Lead can waive (S20 notes).
        deepLinkPattern: '/interviews/:interviewId/scorecards',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    {
        key: 'decision.escalation_hr_lead',
        catalogueId: 'N-43',
        category: 'scorecards_decisions',
        label: 'Decision overdue — HR told',
        description: 'A round decision is three working days late, so HR is told.',
        channels: ['email'],
        deepLinkPattern: '/interviews/:interviewId/debrief',
        isEscalation: true,
        isUrgent: true,
        templateKey: 'escalation.generic',
    },
    // S28 (Q18): §L22's NOTIF-04 is built by no slice, so N-70 opens RPT-01 at its breach section, on that day.
    {
        key: 'sla.breach_summary',
        catalogueId: 'N-70',
        category: 'system_admin',
        label: 'Daily SLA breach summary',
        description: 'Everything that breached its target yesterday, in one email.',
        channels: ['email'],
        deepLinkPattern: '/reports/command-center?period=custom&from=:from&to=:to&section=breaches',
        isEscalation: true,
        templateKey: 'report.breach_summary',
    },
    {
        key: 'report.morning_digest',
        catalogueId: 'N-71',
        category: 'system_admin',
        label: 'Morning digest',
        description: 'At 8am, the command center in one email: open requisitions, this month\'s funnel, breaches, hires, time to fill and cost per hire.',
        channels: ['email'],
        deepLinkPattern: '/reports/command-center',
        templateKey: 'report.morning_digest',
    },
    // S27: the workspace in an email (master plan "daily digest email mirroring this screen").
    {
        key: 'workspace.digest',
        catalogueId: 'N-72',
        category: 'system_admin',
        label: 'Recruiter daily digest',
        description: 'At 8am, your workspace in one email: CVs to sort, interviews today, decisions, overdue tasks, offers and SLA warnings.',
        channels: ['email'],
        deepLinkPattern: '/',
        templateKey: 'workspace.digest',
    },
    // S29 (Q4): withdrawals recorded on the candidate's behalf now exist, so §L22's two rows fire.
    {
        key: 'application.withdrawn',
        catalogueId: 'N-60',
        category: 'candidate_pool',
        label: 'Candidate withdrew',
        description: 'Someone else recorded that a candidate on one of your applications withdrew.',
        channels: ['in_app'],
        deepLinkPattern: '/applications/:applicationId',
    },
    {
        key: 'application.withdrawal_accountability',
        catalogueId: 'N-61',
        category: 'candidate_pool',
        label: 'Withdrawal over the process or experience',
        description: 'A candidate withdrew because the process took too long or the experience was poor.',
        channels: ['email'],
        deepLinkPattern: '/reports/drop-off?section=accountability',
        templateKey: 'application.withdrawal_accountability',
    },
    // S29 (Q9): F17.05's scheduled delivery — §L22 had no row for it.
    {
        key: 'report.scheduled',
        catalogueId: 'N-84',
        category: 'system_admin',
        label: 'Scheduled report',
        description: 'A report you scheduled, with the spreadsheet attached.',
        channels: ['email'],
        deepLinkPattern: '/reports/builder?saved=:savedReportId',
        templateKey: 'report.scheduled',
    },
    // S30 (Q15): D15's "emailed to the admin/HR Lead address" — §L22 had no row for it.
    {
        key: 'retention.archive_ready',
        catalogueId: 'N-85',
        category: 'system_admin',
        label: 'Monthly candidate archive',
        description: 'On the 1st: who is due for anonymisation this month, with the Excel file attached.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/admin/retention?archive=:archiveId',
        templateKey: 'retention.archive_ready',
    },
    // --- R4: CSV import (§L29) ------------------------------------------------------
    {
        key: 'import.finished',
        catalogueId: 'N-86',
        category: 'application_screening',
        label: 'Import finished',
        description: 'An import you started has finished: how many were imported, and which CVs need you.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/import?import=:importId',
    },
    // --- R5: ATS ranking (§L30) ----------------------------------------------------
    {
        key: 'import.ranked',
        catalogueId: 'N-87',
        category: 'application_screening',
        label: 'Imported candidates ranked',
        description: "Every candidate from an import on your job has been ranked, so you can start with the top fits.",
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/ranking?tab=top_fit',
    },
    // --- R6: the shortlist hand-off (§L31) ---------------------------------------------
    {
        key: 'shortlist.ready',
        catalogueId: 'N-88',
        category: 'application_screening',
        label: 'Shortlist ready',
        description: 'HR has sent you a shortlist for a job you asked for or manage: say yes or no to each person.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/my/shortlists/:jobId',
        templateKey: 'shortlist.update',
    },
    {
        key: 'shortlist.added',
        catalogueId: 'N-89',
        category: 'application_screening',
        label: 'More candidates on your shortlist',
        description: 'HR has added people to a shortlist you were sent. The link opens just the new names.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/my/shortlists/:jobId?send=:shortlistId',
        templateKey: 'shortlist.update',
    },
    {
        key: 'shortlist.reminder',
        catalogueId: 'N-90',
        category: 'application_screening',
        label: 'Shortlist still waiting',
        description: 'A shortlist you were sent two working days ago is still waiting for your answers. Sent once.',
        channels: ['email'],
        deepLinkPattern: '/my/shortlists/:jobId',
        templateKey: 'shortlist.update',
    },
    {
        key: 'shortlist.finished',
        catalogueId: 'N-91',
        category: 'application_screening',
        label: 'Manager finished the shortlist',
        description: 'The requester or line manager pressed Done on a shortlist for your job: how many said yes and no.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/shortlist',
        templateKey: 'shortlist.finished',
    },
    // --- R8: Selected (§L33, F24.11) ----------------------------------------------------
    // One §L22 row, two links (DECISIONS-LOG "Start R8" Q3): the recruiter opens JOB-12 at
    // Selected; the requester and line manager, whose reach is the shortlist (§L23), open SHL-01.
    {
        key: 'selection.made',
        catalogueId: 'N-92',
        category: 'scorecards_decisions',
        label: 'Candidate selected',
        description: 'Someone was selected for a job you recruit for, asked for or manage.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/selected',
        templateKey: 'selection.made',
    },
    {
        key: 'selection.made_for_manager',
        catalogueId: 'N-92',
        category: 'scorecards_decisions',
        label: 'Candidate selected (your shortlist)',
        description: 'Someone you said yes to on a shortlist was selected for the job.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/my/shortlists/:jobId',
        templateKey: 'selection.made',
    },
    // --- R1: Master settings (§L28) ------------------------------------------------
    {
        key: 'settings.module_toggled',
        catalogueId: 'N-93',
        category: 'system_admin',
        label: 'A feature switched on or off',
        description: 'Another admin switched a part of the app on or off in Master settings.',
        // In-app only: the other HR Leads need to know, not to act.
        channels: ['in_app'],
        deepLinkPattern: '/admin/master-settings',
    },
    // --- Ranking 2.0 Part 1: remarks (ADR 0049) --------------------------------------
    // One §L22 row, two links (DECISIONS-LOG 2026-10-17 "Ranking 2.0 Part 1" Q4): HR opens RNK-01 with the
    // candidate's sheet open; a manager, whose reach is the shortlist (§L23), opens SHL-01 — and only once
    // the person was sent to them; until then the mention waits.
    {
        key: 'remark.mentioned',
        catalogueId: 'N-94',
        category: 'application_screening',
        label: 'Mentioned in a remark',
        description: 'Someone @mentioned you in a remark on a candidate for a job you work on.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/ranking?app=:applicationId',
    },
    {
        key: 'remark.mentioned_for_manager',
        catalogueId: 'N-94',
        category: 'application_screening',
        label: 'Mentioned in a remark (your shortlist)',
        description: 'HR @mentioned you in a remark on someone on a shortlist they sent you.',
        channels: ['in_app'],
        deepLinkPattern: '/my/shortlists/:jobId',
    },
    // --- ADR 0070 stage 3: a job's Jev budget. All land on the job's Jev budget panel on Ranking (the `#jev-budget` anchor scrolls to it).
    {
        key: 'job.jev_budget_warned',
        catalogueId: 'N-95',
        category: 'job_posting',
        label: 'Jev budget 80% used',
        description: 'A job’s Jev budget is four-fifths spent. Reading stops when it runs out.',
        channels: ['in_app'],
        deepLinkPattern: '/jobs/:jobId/ranking#jev-budget',
    },
    {
        key: 'job.jev_budget_used_up',
        catalogueId: 'N-96',
        category: 'job_posting',
        label: 'Jev budget used up',
        description: 'A job’s Jev budget is spent, so Jev has stopped reading for it. Ask for more, or raise it.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/ranking#jev-budget',
        isUrgent: true,
        templateKey: 'job.update',
    },
    {
        key: 'job.jev_budget_asked',
        catalogueId: 'N-97',
        category: 'job_posting',
        label: 'More Jev budget asked for',
        description: 'A recruiter asked HR for more Jev budget on a job.',
        channels: ['in_app', 'email'],
        deepLinkPattern: '/jobs/:jobId/ranking#jev-budget',
        isUrgent: true,
        templateKey: 'job.update',
    },
];
const BY_KEY = new Map(exports.NOTIFICATION_DEFINITIONS.map((definition) => [definition.key, definition]));
function notificationDefinition(key) {
    return BY_KEY.get(key);
}
/** The escalations inside one category — the locked rows NOTIF-02 lists. */
function escalationsIn(category) {
    return exports.NOTIFICATION_DEFINITIONS.filter((definition) => definition.category === category && definition.isEscalation === true);
}
/**
 * Fills a deep-link pattern.
 *
 * Non-negotiable #3, made mechanical. A missing parameter throws rather than
 * producing `/admin/users/:userId`, which would render as a 404 for the recipient
 * and look like a broken screen rather than a broken notification.
 */
function buildDeepLink(pattern, params) {
    return pattern.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_match, name) => {
        const value = params[name];
        if (value === undefined || value === '') {
            throw new Error(`Deep link "${pattern}" needs a "${name}" parameter.`);
        }
        return encodeURIComponent(value);
    });
}
// --- Requests ----------------------------------------------------------------
exports.notificationCategorySchema = zod_1.z.enum(exports.NOTIFICATION_CATEGORIES);
exports.inboxQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(100).default(30),
    /** `unread` is the default view: an inbox showing read items first is a list, not an inbox. */
    filter: zod_1.z.enum(['all', 'unread']).default('all'),
    category: exports.notificationCategorySchema.optional(),
});
exports.markReadSchema = zod_1.z.object({
    /** Omitted means "everything currently unread". */
    ids: zod_1.z.array(schemas_1.uuidSchema).max(200).optional(),
});
exports.updatePreferencesSchema = zod_1.z.object({
    categories: zod_1.z
        .array(zod_1.z.object({
        category: exports.notificationCategorySchema,
        isInAppEnabled: zod_1.z.boolean(),
        isEmailEnabled: zod_1.z.boolean(),
    }))
        .max(exports.NOTIFICATION_CATEGORIES.length),
    isDigestEnabled: zod_1.z.boolean(),
});
/** The HR Lead's "does any of this actually work" button. QA test 1 and 5. */
exports.sendTestNotificationSchema = zod_1.z.object({
    /** Defaults to the caller — an HR Lead checking their own inbox and mailbox. */
    recipientUserId: schemas_1.uuidSchema.optional(),
    /** Also fire an escalation after this many seconds, to prove the delay path. */
    escalateAfterSeconds: zod_1.z.coerce.number().int().min(5).max(3600).optional(),
});
// The email delivery log (ADMIN Email delivery) is in `email-delivery.ts`.
//# sourceMappingURL=notifications.js.map